import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';

const ProjectContext = createContext();

export const KNOWN_PROJECTS = [
  { id: 'bcbf2026', name: 'BCBF 2026', description: 'Bologna Children\'s Book Fair 2026', isDefault: false, isActive: true, order: 0 },
  { id: 'fbf2025', name: 'FBF 2025', description: 'Frankfurt Book Fair 2025', isDefault: false, isActive: true, order: 1 },
  { id: 'fbf2024', name: 'FBF 2024', description: 'Frankfurt Book Fair 2024', isDefault: false, isActive: true, order: 2 }
];

export const DEFAULT_PROJECT = 'fbf2025';

export const isValidProject = (projectId, projectList) => {
  const list = projectList || KNOWN_PROJECTS;
  return list.some(p => p.id === projectId);
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState(KNOWN_PROJECTS);
  const [activeProject, setActiveProject] = useState(DEFAULT_PROJECT);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const location = useLocation();

  // Load projects from Firestore on mount, fall back to KNOWN_PROJECTS
  useEffect(() => {
    let cancelled = false;

    const loadProjects = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'projects'));
        if (cancelled) return;

        const firestoreProjects = [];
        querySnapshot.forEach((doc) => {
          firestoreProjects.push({ id: doc.id, ...doc.data() });
        });

        // Merge Firestore projects with KNOWN_PROJECTS so hardcoded ones are never lost
        const merged = [...KNOWN_PROJECTS];
        for (const fp of firestoreProjects) {
          const idx = merged.findIndex(p => p.id === fp.id);
          if (idx >= 0) {
            merged[idx] = { ...merged[idx], ...fp };
          } else {
            merged.push(fp);
          }
        }

        const activeList = merged
          .filter(p => p.isActive !== false)
          .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));

        setProjects(activeList);

        const defaultProj = activeList.find(p => p.isDefault);
        if (defaultProj) {
          const pathSegment = location.pathname.split('/')[1];
          const isUrlProject = activeList.some(p => p.id === pathSegment);
          if (!isUrlProject) {
            setActiveProject(defaultProj.id);
          }
        }
      } catch (error) {
        console.warn('Failed to load projects from Firestore, using fallback:', error.message);
      } finally {
        if (!cancelled) setProjectsLoaded(true);
      }
    };

    const timeout = setTimeout(() => {
      if (!cancelled && !projectsLoaded) {
        setProjectsLoaded(true);
      }
    }, 5000);

    loadProjects();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  // Detect active project from URL path
  useEffect(() => {
    const pathSegment = location.pathname.split('/')[1];
    if (pathSegment && isValidProject(pathSegment, projects)) {
      setActiveProject(pathSegment);
    }
  }, [location.pathname, projects]);

  const getProjectPath = useCallback((path) => {
    return `/${activeProject}${path}`;
  }, [activeProject]);

  const isValid = useCallback((projectId) => {
    return isValidProject(projectId, projects);
  }, [projects]);

  const defaultProject = useMemo(() => {
    const def = projects.find(p => p.isDefault);
    return def ? def.id : (projects[0]?.id || DEFAULT_PROJECT);
  }, [projects]);

  const value = useMemo(() => ({
    activeProject,
    setActiveProject,
    projects,
    defaultProject,
    projectsLoaded,
    isValidProject: isValid,
    getProjectPath
  }), [activeProject, projects, defaultProject, projectsLoaded, isValid, getProjectPath]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
